import { danger } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { createReducer } from 'utils/redux';
import { getAccessParams } from 'utils/shared-access';
import { API, get as getRequest, put } from 'utils/api';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';

import { fetchCampaign } from './clientApproval';

import type {
  Action,
  ClientApprovalBucketItemV2,
  ClientApprovalBucketUpdatePayload,
  ClientApprovalBucketV2,
  ClientApprovalGlobalStateV2,
  ThunkAction
} from 'types';

applyTranslations(require('./client-approval-v2-reducer.i18n.json'));

const RESET = 'clientApprovalV2/RESET';
const DATA_LOADING = 'clientApprovalV2/DATA_LOADING';
const MARK_FAVOURITE_LOADING = 'clientApprovalV2/MARK_FAVOURITE_LOADING';
const DATA_LOADED = 'clientApprovalV2/DATA_LOADED';
const UPDATE_BUCKET = 'clientApprovalV2/UPDATE_BUCKET';

const initialState: ClientApprovalGlobalStateV2 = {
  isLoading: false,
  markAsFavLoadingAsset: {},
  list: [],
  pagination: {}
};

export function resetClientApproval(): Action {
  return {
    type: RESET
  };
}

function dataLoading(isLoading: boolean): Action {
  return {
    type: DATA_LOADING,
    payload: {
      isLoading
    }
  };
}

function markAsFavLoading(isLoading: boolean, assetId: number): Action {
  return {
    type: MARK_FAVOURITE_LOADING,
    payload: {
      isLoading,
      assetId
    }
  };
}

function dataLoaded(data: any): Action {
  return {
    type: DATA_LOADED,
    payload: {
      data
    }
  };
}

function updateBucket(bucketId: number, payload: ClientApprovalBucketV2): Action {
  return {
    type: UPDATE_BUCKET,
    payload: {
      bucketId,
      payload
    }
  };
}

export function fetchClientApproval(influencerId: number, page: number, sort: string): ThunkAction {
  return async (dispatch, getState) => {
    try {
      const { config, options } = getAccessParams(getState);
      const url = `${API.END_POINTS.SHARED_ACCESS.fetchBucketsV2(String(influencerId || ''), page, sort)}`;

      dispatch(dataLoading(true));
      const response = await getRequest(url, config, options);
      dispatch(dataLoaded(response.data));
    } catch (error) {
      logError(error);
    } finally {
      dispatch(dataLoading(false));
    }
  };
}

export function updateBucketStatus(bucketId: number, payload: ClientApprovalBucketUpdatePayload): ThunkAction {
  const loaderId = 'updateBucketStatusV2()';

  return async (dispatch, getState) => {
    try {
      dispatch(showLoading(loaderId));

      const { config, options } = getAccessParams(getState);
      const url = API.END_POINTS.SHARED_ACCESS.updateBucketNotesV2(bucketId);
      const { data } = await put(url, { ...config, data: payload }, options);

      dispatch(updateBucket(bucketId, data));
      dispatch(fetchCampaign());
    } catch (error) {
      logError(error);
      dispatch(danger(__t('client_approval_v2_reducer.error_profile_update_error')));
    } finally {
      dispatch(hideLoading(loaderId));
    }
  };
}

export function markAsFavourite(markAsFav: boolean, bucketId: number, itemId: number): ThunkAction {
  return async (dispatch, getState) => {
    const url = API.END_POINTS.SHARED_ACCESS.markAsFavourite(bucketId, itemId);

    try {
      const { config, options } = getAccessParams(getState);
      const { list } = getState().clientApprovalV2;

      dispatch(markAsFavLoading(true, itemId));

      await put(url, { ...config,
        data: {
          favourite: markAsFav
        } }, options);

      const bucket = list.find(b => b.id === bucketId);
      const items: Array<ClientApprovalBucketItemV2> = (bucket?.items || []).map((item) => {
        if (item.id === itemId) {
          return {
            ...item,
            favourite: markAsFav
          };
        }

        return item;
      });

      if (bucket) {
        dispatch(updateBucket(bucketId, {
          ...bucket,
          items: items || []
        }));
      }
    } catch (error) {
      logError(error);

      dispatch(danger(__t(`client_approval_v2_reducer.error_favourite.${error.response.status == 409 ? '409' : 'generic'}`)));
    } finally {
      dispatch(markAsFavLoading(false, itemId));
    }
  };
}

export function markMessagesAsRead(bucketId: number, ids: Array<number>): ThunkAction {
  return (dispatch, getState) => {
    try {
      const { config, options } = getAccessParams(getState);
      const url = API.END_POINTS.SHARED_ACCESS.markNotesAsReadV2(bucketId);
      put(url, { ...config, data: { ids } }, options);
    } catch (error) {
      logError(error);
    }
  };
}

function remapListWithTaskNames(list: Array<ClientApprovalBucketV2>) {
  const taskTypeTitleIndices = {};

  return list.map(entry => {
    if (entry.taskName) {
      return entry;
    }

    // calculate fallback task name as task type + task index
    const index = entry.taskIndex ?? ((taskTypeTitleIndices[entry.taskType] ?? 0) + 1);
    taskTypeTitleIndices[entry.taskType] = index;
    const taskName = __t(`collabary_common.types.${entry.taskType}.singular`);
    const fallbackTaskName = `${taskName} #${index}`;

    return ({
      ...entry,
      taskName: fallbackTaskName
    });
  });
}

export default createReducer<ClientApprovalGlobalStateV2, any>('clientApprovalV2', initialState, {
  [RESET]: () => initialState,
  [DATA_LOADING]: (state, { isLoading }) => ({
    ...state,
    isLoading
  }),
  [DATA_LOADED]: (state, { data }) => ({
    ...state,
    list: remapListWithTaskNames(data.data),
    pagination: {
      ...data.meta,
      links: data.links
    }
  }),
  [UPDATE_BUCKET]: (state, { bucketId, payload }) => ({
    ...state,
    list: state.list.map(
      bucket => (
        bucketId === bucket.id
          ? { ...payload }
          : bucket
      )
    )
  }),
  [MARK_FAVOURITE_LOADING]: (state, { isLoading, assetId }) => ({
    ...state,
    markAsFavLoadingAsset: {
      ...state.markAsFavLoadingAsset,
      [assetId]: isLoading
    }
  })
});
